import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from '../api';
import { loadStoredAuth } from '../authStorage';
import { Button } from '../components/ui/Button';
import { ErrorState } from '../components/ui/ErrorState';
import { Skeleton } from '../components/ui/Skeleton';
import { hasFrontendPermission } from '../permissions';

// ── Helpers (copied from App.js) ─────────────────────────────────────────────

const toDateAtMidnight = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return null;
  const parts = raw.split('-').map(Number);
  if (parts.length !== 3 || parts.some(Number.isNaN)) return null;
  const [y, m, d] = parts;
  return new Date(y, m - 1, d);
};

const startOfToday = () => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), now.getDate());
};

const isOpenJobStatus = (status) => String(status || '').toLowerCase() !== 'completed';

const DEFAULT_DISPATCH_SETTINGS = Object.freeze({
  maxJobsPerTechnicianPerDay: 2,
  slaDueSoonDays: 1,
});

const normalizeDispatchSettings = (value = {}) => {
  const maxJobs = Number(value.maxJobsPerTechnicianPerDay);
  const dueSoonDays = Number(value.slaDueSoonDays);
  return {
    maxJobsPerTechnicianPerDay: Number.isFinite(maxJobs) ? Math.max(1, Math.min(20, Math.floor(maxJobs))) : DEFAULT_DISPATCH_SETTINGS.maxJobsPerTechnicianPerDay,
    slaDueSoonDays: Number.isFinite(dueSoonDays) ? Math.max(0, Math.min(14, Math.floor(dueSoonDays))) : DEFAULT_DISPATCH_SETTINGS.slaDueSoonDays,
  };
};

const computeDispatchInsights = (jobs, rawSettings = DEFAULT_DISPATCH_SETTINGS) => {
  const settings = normalizeDispatchSettings(rawSettings);
  const today = startOfToday();
  const byTechDay = new Map();
  const conflicts = [];
  const slaRisks = [];
  let unassignedOpen = 0;

  (jobs || []).forEach((job) => {
    const scheduledDate = String(job?.scheduledDate || '').trim();
    const assignedTo = String(job?.assignedTo || '').trim();
    const status = String(job?.status || '').toLowerCase();
    const isOpen = isOpenJobStatus(status);
    const dateObj = toDateAtMidnight(scheduledDate);

    if (isOpen && !assignedTo) unassignedOpen += 1;

    if (isOpen && assignedTo && scheduledDate) {
      const key = `${assignedTo}::${scheduledDate}`;
      const list = byTechDay.get(key) || [];
      list.push(job);
      byTechDay.set(key, list);
    }

    if (!isOpen || !dateObj) return;

    if (dateObj < today) { slaRisks.push({ severity: 'high', job }); return; }

    const dueSoonCutoff = new Date(today.getTime() + settings.slaDueSoonDays * 86400000);
    if ((status === 'new' || status === 'assigned') && dateObj <= dueSoonCutoff) {
      slaRisks.push({ severity: 'medium', job });
    }
  });

  for (const [key, list] of byTechDay.entries()) {
    if (list.length <= settings.maxJobsPerTechnicianPerDay) continue;
    const [technician, date] = key.split('::');
    const overflow = list.length - settings.maxJobsPerTechnicianPerDay;
    conflicts.push({ technician, date, count: list.length, severity: overflow >= 2 ? 'high' : 'medium', jobs: list });
  }

  conflicts.sort((a, b) => b.count - a.count);
  slaRisks.sort((a, b) => (a.severity === b.severity ? 0 : a.severity === 'high' ? -1 : 1));
  return { conflicts, slaRisks, unassignedOpen };
};

const buildJobsDrilldownQuery = ({ riskType, technician, date, jobId, dueSoonDays }) => {
  const params = new URLSearchParams();
  if (riskType) params.set('riskType', String(riskType));
  if (technician) params.set('technician', String(technician));
  if (date) params.set('date', String(date));
  if (jobId) params.set('jobId', String(jobId));
  if (dueSoonDays !== undefined && dueSoonDays !== null && dueSoonDays !== '') params.set('dueSoonDays', String(dueSoonDays));
  const query = params.toString();
  return query ? `?${query}` : '';
};

// ── SVG Icons ────────────────────────────────────────────────────────────────

const RefreshIcon = () => (
  <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
    <path d="M11.5 6.5A5 5 0 1 1 9 2.1" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
    <path d="M9 1v3h3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const ArrowRightIcon = () => (
  <svg width="11" height="11" viewBox="0 0 11 11" fill="none">
    <path d="M2 5.5h7M6 2.5l3 3-3 3" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

// ── Sub-components ────────────────────────────────────────────────────────────

function StatCard({ label, value, accent, sub }) {
  return (
    <div className="db-stat-card">
      <span className="db-stat-label">{label}</span>
      <span className="db-stat-value" style={accent ? { color: accent } : undefined}>{value ?? '—'}</span>
      {sub && <span className="db-stat-sub">{sub}</span>}
    </div>
  );
}

function SectionHeader({ title }) {
  return <h2 className="db-section-title">{title}</h2>;
}

function RiskItem({ severity, title, detail, onClick }) {
  return (
    <button type="button" className={`db-risk-item db-risk-item--${severity}`} onClick={onClick}>
      <div className="db-risk-content">
        <span className="db-risk-title">{title}</span>
        <span className="db-risk-detail">{detail}</span>
      </div>
      <ArrowRightIcon />
    </button>
  );
}

function KpiCard({ label, value }) {
  return (
    <div className="db-kpi-card">
      <span className="db-kpi-label">{label}</span>
      <strong className="db-kpi-value">{value ?? '—'}</strong>
    </div>
  );
}

function TechCard({ tech }) {
  const initials = String(tech.username || '?').slice(0, 2).toUpperCase();
  const completionRate = tech.totalJobs > 0
    ? Math.round((tech.completed / tech.totalJobs) * 100)
    : 0;

  return (
    <div className="db-tech-card">
      <div className="db-tech-avatar">{initials}</div>
      <div className="db-tech-info">
        <span className="db-tech-name">{tech.username}</span>
        <div className="db-tech-row">
          <span className="db-tech-stat">{tech.totalJobs} jobs</span>
          <span className="db-tech-stat db-tech-stat--done">{tech.completed} done</span>
          {tech.inProgress > 0 && (
            <span className="db-tech-stat db-tech-stat--active">{tech.inProgress} active</span>
          )}
        </div>
        <div className="db-tech-bar-wrap">
          <div className="db-tech-bar-fill" style={{ width: `${completionRate}%` }} />
        </div>
      </div>
      <span className="db-tech-pct">{completionRate}%</span>
    </div>
  );
}

function CategoryBar({ name, count, max }) {
  const pct = max > 0 ? Math.round((count / max) * 100) : 0;
  return (
    <div className="db-cat-row">
      <div className="db-cat-meta">
        <span className="db-cat-name">{name}</span>
        <span className="db-cat-count">{count}</span>
      </div>
      <div className="db-cat-track">
        <div className="db-cat-fill" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function ActivityItem({ activity }) {
  return (
    <div className="db-activity-row">
      <span className="db-activity-dot" />
      <div className="db-activity-body">
        <span className="db-activity-desc">{activity.description}</span>
        <span className="db-activity-time">
          {new Date(activity.timestamp).toLocaleString(undefined, {
            month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
          })}
        </span>
      </div>
    </div>
  );
}

const ROLE_GUIDANCE = Object.freeze({
  admin: {
    title: 'Run the business from the overview first.',
    summary: 'Check operating health, billing movement, and workflow risk here before dropping into deeper admin pages.',
    checkpoints: [
      'Review the risk board for overdue or conflicting work.',
      'Check revenue and pending invoices before opening billing tools.',
      'Use analytics or settings only after you know the operational bottleneck.',
    ],
  },
  manager: {
    title: 'Use the dashboard to spot pressure before reassigning work.',
    summary: 'The fastest path is to identify slipping jobs, team overload, and project pressure before you start clicking through records.',
    checkpoints: [
      'Clear today’s dispatch or deadline risk first.',
      'Review project and team workload before moving assignments.',
      'Keep invoices and customer follow-up moving once field work is stable.',
    ],
  },
  dispatcher: {
    title: 'Start with dispatch, then move through jobs and schedule.',
    summary: 'Triage unassigned or overdue work first, confirm the day plan second, and open individual records only when you need detail.',
    checkpoints: [
      'Look for unassigned jobs or schedule conflicts.',
      'Open jobs to clear blockers and technician handoffs.',
      'Use customer records when you need context or follow-up.',
    ],
  },
});

function buildDashboardActions(user) {
  const candidates = [
    { label: 'Open Dispatch', to: '/dispatch', permission: 'dispatch.manage' },
    { label: 'Review Jobs', to: '/jobs', permission: 'jobs.manage' },
    { label: 'Plan Schedule', to: '/schedule', permission: 'dispatch.manage' },
    { label: 'Check Customers', to: '/customers', permission: 'customers.manage' },
    { label: 'Review Quotes', to: '/quotes', permission: 'quotes.manage' },
    { label: 'Review Invoices', to: '/invoices', permission: 'invoices.manage' },
    { label: 'Open Projects', to: '/projects', permission: 'projects.manage' },
    { label: 'View Team Load', to: '/team', permission: 'technicians.manage' },
    { label: 'Open Analytics', to: '/analytics', permission: 'exports.view' },
    { label: 'Update Settings', to: '/settings', permission: 'dispatch.manage' },
  ];

  const role = String(user?.role || '').toLowerCase();
  const preferredOrder = role === 'admin'
    ? ['Open Dispatch', 'Open Analytics', 'Review Invoices', 'Update Settings']
    : role === 'manager'
      ? ['Open Dispatch', 'Open Projects', 'View Team Load', 'Review Invoices']
      : ['Open Dispatch', 'Review Jobs', 'Plan Schedule', 'Check Customers'];

  const visible = candidates.filter((item) => !item.permission || hasFrontendPermission(user, item.permission));
  const ranked = preferredOrder
    .map((label) => visible.find((item) => item.label === label))
    .filter(Boolean);
  const remainder = visible.filter((item) => !ranked.includes(item));
  return [...ranked, ...remainder].slice(0, 4);
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export function DashboardPage() {
  const navigate = useNavigate();
  const auth = loadStoredAuth();
  const token = auth?.token;
  const user = auth?.user;

  const [summary, setSummary] = useState(null);
  const [kpis, setKpis] = useState(null);
  const [scheduledJobs, setScheduledJobs] = useState([]);
  const [dispatchSettings, setDispatchSettings] = useState(DEFAULT_DISPATCH_SETTINGS);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    if (!token) { navigate('/login'); return; }
    let cancelled = false;
    setLoading(true);
    setError('');
    Promise.all([
      apiFetch('/api/dashboard/summary', { token }),
      apiFetch('/api/schedule', { token }),
      apiFetch('/api/settings/dispatch', { token }),
      apiFetch('/api/dashboard/kpis', { token }),
    ]).then(([summaryData, scheduleData, settingsData, kpiData]) => {
      if (cancelled) return;
      setSummary(summaryData);
      setKpis(kpiData || null);
      setScheduledJobs(Array.isArray(scheduleData) ? scheduleData : []);
      setDispatchSettings(normalizeDispatchSettings(settingsData || {}));
    }).catch((e) => {
      if (!cancelled) setError(e.message || 'Failed to load dashboard');
    }).finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => { cancelled = true; };
  }, [token, refreshKey, navigate]);

  const insights = useMemo(
    () => computeDispatchInsights(scheduledJobs, dispatchSettings),
    [scheduledJobs, dispatchSettings],
  );

  const handleRiskDrilldown = useCallback((payload) => {
    navigate(`/jobs${buildJobsDrilldownQuery(payload)}`);
  }, [navigate]);

  const hasRisks = insights.conflicts.length > 0 || insights.slaRisks.length > 0 || insights.unassignedOpen > 0;
  const categoryMax = summary ? Math.max(...Object.values(summary.categoryStats || {}), 1) : 1;
  const role = String(user?.role || '').toLowerCase();
  const guidance = ROLE_GUIDANCE[role] || ROLE_GUIDANCE.dispatcher;
  const quickActions = useMemo(() => buildDashboardActions(user), [user]);

  return (
    <div className="db-page">
      {/* ── Header ── */}
      <div className="db-header">
        <div>
          <h1 className="db-heading">Dashboard</h1>
          {user && (
            <p className="db-subheading">
              {user.username} · <span style={{ textTransform: 'capitalize' }}>{user.role}</span>
            </p>
          )}
        </div>
        <Button variant="secondary" onClick={() => setRefreshKey((k) => k + 1)} disabled={loading}>
          <RefreshIcon />
          Refresh
        </Button>
      </div>

      {error && <ErrorState message={error} onRetry={() => setRefreshKey((k) => k + 1)} />}

      {loading ? (
        <Skeleton lines={8} />
      ) : !summary ? (
        <ErrorState title="No data" message="Dashboard data is unavailable." />
      ) : (
        <div className="db-body">
          <section className="db-guide-card">
            <div className="db-guide-header">
              <div>
                <span className="db-guide-kicker">Start Here</span>
                <h2 className="db-guide-title">{guidance.title}</h2>
                <p className="db-guide-copy">{guidance.summary}</p>
              </div>
              <div className="db-guide-actions">
                {quickActions.map((action) => (
                  <Button key={action.to} variant="secondary" size="sm" onClick={() => navigate(action.to)}>
                    {action.label}
                  </Button>
                ))}
              </div>
            </div>
            <div className="db-guide-checklist">
              {guidance.checkpoints.map((item) => (
                <div key={item} className="db-guide-checkpoint">
                  <span className="db-guide-checkpoint-dot" aria-hidden="true" />
                  <span>{item}</span>
                </div>
              ))}
            </div>
          </section>

          {/* ── Stat strip ── */}
          <div className="db-stats-grid">
            <StatCard label="Total Jobs"        value={summary.totalJobs}         accent="var(--text-primary)" />
            <StatCard label="New"               value={summary.newJobs}           accent="var(--accent)" />
            <StatCard label="In Progress"       value={summary.inProgressJobs}    accent="var(--info)" />
            <StatCard label="Completed"         value={summary.completedJobs}     accent="var(--success)" />
            {summary.totalCustomers !== undefined && (
              <StatCard label="Customers"       value={summary.totalCustomers} />
            )}
            {summary.totalRevenue !== undefined && (
              <StatCard label="Revenue"         value={`$${summary.totalRevenue.toFixed(2)}`} accent="var(--success)" />
            )}
            {summary.pendingInvoices !== undefined && (
              <StatCard label="Pending Invoices" value={summary.pendingInvoices}  accent="var(--warning)" />
            )}
            <StatCard
              label="Deadline Risks"
              value={insights.slaRisks.length}
              accent={insights.slaRisks.length > 0 ? 'var(--danger)' : undefined}
            />
            <StatCard
              label="Dispatch Conflicts"
              value={insights.conflicts.length}
              accent={insights.conflicts.length > 0 ? 'var(--warning)' : undefined}
            />
          </div>

          {/* ── Risk board ── */}
          {hasRisks && (
            <section className="db-section">
              <SectionHeader title="Risk Board" />
              <p className="db-section-sub">Jobs that are overdue, conflicting, or unassigned.</p>
              <div className="db-risk-grid">
                {insights.conflicts.slice(0, 5).map((c) => (
                  <RiskItem
                    key={`${c.technician}-${c.date}`}
                    severity={c.severity}
                    title="Scheduling Conflict"
                    detail={`${c.technician} — ${c.count} jobs on ${c.date}`}
                    onClick={() => handleRiskDrilldown({ riskType: 'conflict', technician: c.technician, date: c.date })}
                  />
                ))}
                {insights.slaRisks.slice(0, 5).map((r) => (
                  <RiskItem
                    key={r.job.id}
                    severity={r.severity}
                    title={r.severity === 'high' ? 'Overdue' : 'Due Today'}
                    detail={`${r.job.id}: ${r.job.title}`}
                    onClick={() => handleRiskDrilldown({
                      riskType: r.severity === 'high' ? 'sla_overdue' : 'sla_due',
                      jobId: r.job.id,
                      dueSoonDays: dispatchSettings.slaDueSoonDays,
                    })}
                  />
                ))}
                {insights.unassignedOpen > 0 && (
                  <RiskItem
                    severity="medium"
                    title="Unassigned Jobs"
                    detail={`${insights.unassignedOpen} open jobs have no technician assigned`}
                    onClick={() => handleRiskDrilldown({ riskType: 'unassigned' })}
                  />
                )}
              </div>
            </section>
          )}

          {/* ── KPIs ── */}
          {kpis && (
            <section className="db-section">
              <SectionHeader title="Operational KPIs" />
              <div className="db-kpi-grid">
                <KpiCard label="On-Time Completion"       value={`${kpis.sla?.onTimeCompletionRatePct ?? 0}%`} />
                <KpiCard label="Overdue Open Jobs"        value={kpis.sla?.overdueOpen ?? 0} />
                <KpiCard label="Jobs Due Soon"            value={kpis.sla?.dueSoonOpen ?? 0} />
                <KpiCard label="Avg Resolution (hrs)"     value={kpis.operations?.avgResolutionHours ?? 0} />
                <KpiCard label="Check-In Compliance"      value={`${kpis.operations?.checkinCompliancePct ?? 0}%`} />
                <KpiCard label="Completion Proof"         value={`${kpis.sla?.completionProofCoveragePct ?? 0}%`} />
                <KpiCard label="Quick Close (7d)"         value={kpis.operations?.quickClose7d ?? 0} />
                <KpiCard label="Backlog 3d+"              value={(kpis.backlog?.agingBuckets?.over7Days ?? 0) + (kpis.backlog?.agingBuckets?.over3Days ?? 0)} />
              </div>
            </section>
          )}

          {/* ── Technician performance ── */}
          {summary.technicianStats?.length > 0 && (
            <section className="db-section">
              <SectionHeader title="Technician Performance" />
              <div className="db-tech-grid">
                {summary.technicianStats.map((tech) => (
                  <TechCard key={tech.username} tech={tech} />
                ))}
              </div>
            </section>
          )}

          {/* ── Jobs by category ── */}
          {summary.categoryStats && Object.keys(summary.categoryStats).length > 0 && (
            <section className="db-section">
              <SectionHeader title="Jobs by Category" />
              <div className="db-cat-list">
                {Object.entries(summary.categoryStats).map(([cat, count]) => (
                  <CategoryBar key={cat} name={cat} count={count} max={categoryMax} />
                ))}
              </div>
            </section>
          )}

          {/* ── Recent activity ── */}
          {summary.recentActivity?.length > 0 && (
            <section className="db-section">
              <SectionHeader title="Recent Activity" />
              <div className="db-activity-list">
                {summary.recentActivity.map((a) => (
                  <ActivityItem key={a.id} activity={a} />
                ))}
              </div>
            </section>
          )}

        </div>
      )}
    </div>
  );
}
